import React, { useRef, useState } from 'react';
import { Camera, Check, Loader2, Send, StickyNote, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import { MentionInput, renderWithMentions } from '@/components/support/MentionInput';
import { AttachmentGallery, uploadTicketFiles } from '@/components/support/TicketAttachments';

const when = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');

/** Card-level notes + photos: every note and image visible on the board, quick note box with photo picker and a Save button. */
export function QuickNote({ ticket, onAdded, onUploaded, onNoteChanged, onNoteDeleted, canDelete }) {
  const [text, setText] = useState('');
  const [mentionIds, setMentionIds] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    const body = text.trim();
    if ((!body && !files.length) || busy) return;
    setBusy(true);
    try {
      const uploaded = files.length ? await uploadTicketFiles(ticket.id, files) : [];
      if (uploaded.length) onUploaded?.(uploaded);
      if (body || uploaded.length) {
        const res = await fetch(`${API_URL}/api/support/tickets/${ticket.id}/notes`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: body || '', mention_user_ids: mentionIds, attachment_ids: uploaded.map((a) => a.id) }),
        });
        if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Could not save note');
        const note = await res.json();
        onAdded?.(note);
        toast.success(note.mentions?.length ? `Saved · pinged ${note.mentions.map((m) => m.name).join(', ')}` : uploaded.length && !body ? `${uploaded.length} photo${uploaded.length === 1 ? '' : 's'} added` : 'Note saved');
      }
      setText(''); setMentionIds([]); setFiles([]);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const toggleDone = async (n) => {
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${ticket.id}/notes/${n.id}/done`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ done: !n.done_at }) });
      if (!res.ok) throw new Error('Could not update note');
      onNoteChanged?.(await res.json());
    } catch (e) { toast.error(e.message); }
  };
  const removeNote = async (n) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${ticket.id}/notes/${n.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Could not delete note');
      onNoteDeleted?.(n.id);
    } catch (e) { toast.error(e.message); }
  };
  const notes = (ticket.notes || []).filter((n) => n.body || n.attachments?.length || n.kind);
  return (
    <div data-testid={`support-quicknote-${ticket.id}`}>
      {(ticket.attachments?.length || 0) > 0 && <div className="mb-1.5"><AttachmentGallery ticketId={ticket.id} attachments={ticket.attachments} size="xs" testPrefix={`support-card-att-${ticket.id}`} /></div>}
      {notes.length > 0 && (
        <div className="mb-1.5 rounded-md bg-amber-50/70 border border-amber-100 px-2 py-1.5 space-y-1" data-testid={`support-card-notes-${ticket.id}`}>
          {notes.map((n) => (
            <div key={n.id} className="group/note flex items-start gap-1" data-testid={`support-card-note-${n.id}`}>
              <button type="button" onClick={() => toggleDone(n)} className={`mt-0.5 w-3.5 h-3.5 rounded border shrink-0 inline-flex items-center justify-center ${n.done_at ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-300 bg-white hover:border-slate-500'}`} title={n.done_at ? 'Mark not done' : 'Mark done'} aria-label={n.done_at ? 'Mark note not done' : 'Mark note done'} data-testid={`support-note-done-${n.id}`}>{n.done_at && <Check className="w-2.5 h-2.5" />}</button>
              <div className={`flex-1 min-w-0 text-[11px] leading-snug text-slate-700 whitespace-pre-wrap break-words ${n.done_at ? 'line-through opacity-60' : ''}`}>
                {n.kind ? <span className="text-[9px] font-bold uppercase px-1 rounded bg-sky-600 text-white mr-1">{n.kind === 'sms' ? 'Text' : 'Email'} → {n.to}</span> : <StickyNote className="w-3 h-3 inline mr-1 -mt-0.5 text-amber-600" />}
                <span className="font-semibold text-slate-800">{n.created_by_name ? n.created_by_name.split(' ')[0] : 'Note'}</span>
                <span className="text-slate-400"> · {when(n.created_at)}</span>{' '}
                {renderWithMentions(n.body) || <em className="text-slate-400">added {n.attachments?.length || 1} photo{(n.attachments?.length || 1) === 1 ? '' : 's'}</em>}
              </div>
              {canDelete && <button type="button" onClick={() => removeNote(n)} className="opacity-0 group-hover/note:opacity-100 focus:opacity-100 text-slate-400 hover:text-rose-600 shrink-0" aria-label="Delete note" data-testid={`support-note-delete-${n.id}`}><Trash2 className="w-3 h-3" /></button>}
            </div>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5" data-testid={`support-quicknote-pending-${ticket.id}`}>
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="relative w-12 h-12 rounded border border-slate-200 overflow-hidden">
              <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
              <button type="button" onClick={() => setFiles((l) => l.filter((_, j) => j !== i))} className="absolute -top-0.5 -right-0.5 bg-white/90 rounded-full p-0.5 text-slate-600 hover:text-rose-600" aria-label="Remove"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-start gap-1.5">
        <div className="relative flex-1 min-w-0">
          <MentionInput single value={text} mentionIds={mentionIds} onChange={({ text: tx, mentionIds: ids }) => { setText(tx); setMentionIds(ids); }} onSubmit={submit}
            placeholder="Quick note…" disabled={busy} testid={`support-quicknote-input-${ticket.id}`} className="!py-1 !text-xs" />
        </div>
        <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files || [])].slice(0, 10)); e.target.value = ''; }} data-testid={`support-quicknote-photo-input-${ticket.id}`} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="h-[30px] w-[30px] rounded-md border border-slate-300 bg-white text-slate-600 inline-flex items-center justify-center hover:bg-slate-50 shrink-0" aria-label="Add photos" title="Add photos" data-testid={`support-quicknote-photo-${ticket.id}`}><Camera className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={submit} disabled={busy || (!text.trim() && !files.length)} className="h-[30px] px-2.5 rounded-md bg-slate-900 text-white text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-40 shrink-0" data-testid={`support-quicknote-save-${ticket.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Save
        </button>
      </div>
    </div>
  );
}
